import React from "react";
import { AdvancedFilterDef, FilterValue } from "./advanced-filter-types";
import { Button } from "@/ui-components/ui/button";
import { Input } from "@/ui-components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Checkbox } from "@/ui-components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui-components/ui/popover";
import { Trash2, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/ui-components/ui/command";

interface AdvancedFilterRowProps {
  filter: FilterValue;
  filterDefs: AdvancedFilterDef[];
  onChange: (newFilter: FilterValue) => void;
  onKeyChange: (newKey: string) => void;
  onRemove: () => void;
  availableKeys: string[]; // Keys not yet used (plus current one)
}

export function AdvancedFilterRow({
  filter,
  filterDefs,
  onChange,
  onKeyChange,
  onRemove,
  availableKeys,
}: AdvancedFilterRowProps) {
  const currentDef = filterDefs.find((d) => d.key === filter.key);

  const renderValueInput = () => {
    if (!currentDef) return null;

    switch (currentDef.type) {
      case "boolean":
        return (
          <div className="flex items-center h-9 px-3 border border-gray-200 rounded-lg bg-white w-full hover:border-gray-300 transition-colors shadow-sm">
             <div className="flex items-center space-x-2">
                <Checkbox 
                  id={`bool-${filter.key}`}
                  checked={filter.value === true}
                  onCheckedChange={(checked) => onChange({ ...filter, value: !!checked })}
                  className="rounded-sm border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 h-4 w-4"
                />
                <label 
                  htmlFor={`bool-${filter.key}`}
                  className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer text-gray-600"
                >
                  {currentDef.label} {filter.value ? "Ja" : "Nein"}
                </label>
             </div>
          </div>
        );

      case "select":
        return (
          <Select
            value={filter.value}
            onValueChange={(val) => onChange({ ...filter, value: val })}
          >
            <SelectTrigger className="h-9 text-xs w-full bg-white border-gray-200 rounded-lg hover:border-gray-300 transition-colors shadow-sm focus:ring-1 focus:ring-blue-500/20">
              <SelectValue placeholder="Auswählen..." />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-200 shadow-lg">
              {currentDef.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs rounded-md">
                  <div className="flex items-center gap-2">
                    {opt.icon && <opt.icon className="h-3.5 w-3.5 text-gray-400" />}
                    {opt.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "multi-select":
        const selectedValues = (Array.isArray(filter.value) ? filter.value : []) as string[];
        return (
           <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="h-9 w-full justify-between px-3 bg-white border-gray-200 rounded-lg hover:border-gray-300 transition-colors shadow-sm"
              >
                <div className="flex-1 truncate text-left mr-2">
                  {selectedValues.length > 0 ? (
                    <span className="text-xs font-medium text-gray-900">
                      {selectedValues
                        .map((val) => currentDef.options?.find((o) => o.value === val)?.label || val)
                        .join(", ")}
                    </span>
                  ) : (
                    <span className="text-gray-400 font-normal text-xs">Wählen...</span>
                  )}
                </div>
                <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-40" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0 rounded-xl shadow-xl border-gray-200" align="start">
              <Command className="rounded-xl">
                <CommandInput placeholder="Suche..." className="h-9 text-xs" />
                <CommandEmpty className="py-4 text-xs text-gray-500">Nichts gefunden.</CommandEmpty>
                <CommandGroup className="p-1 max-h-[200px] overflow-y-auto custom-scrollbar">
                  {currentDef.options?.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => {
                        const newValues = selectedValues.includes(opt.value)
                          ? selectedValues.filter((v) => v !== opt.value)
                          : [...selectedValues, opt.value];
                        onChange({ ...filter, value: newValues });
                      }}
                      className="text-xs rounded-md cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-3.5 w-3.5 text-blue-600 transition-opacity",
                          selectedValues.includes(opt.value) ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {opt.icon && <opt.icon className="mr-2 h-3.5 w-3.5 text-gray-400" />}
                      {opt.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        );

      case "text":
      default:
        return (
          <Input
            value={filter.value || ""}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
            placeholder="Wert..."
            className="h-9 text-xs bg-white border-gray-200 rounded-lg hover:border-gray-300 transition-colors shadow-sm focus-visible:ring-1 focus-visible:ring-blue-500/20"
          />
        );
    }
  };

  return (
    <div className="group flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full p-3 sm:p-2 rounded-xl bg-gray-50/50 border border-gray-100 transition-colors hover:border-gray-200 hover:bg-gray-50 shadow-sm sm:shadow-none">
      {/* Attribute Selector & Mobile Remove Button */}
      <div className="flex items-center gap-2 w-full sm:w-[150px] shrink-0">
        <div className="flex-1 min-w-0">
          <Select
            value={filter.key}
            onValueChange={(val) => {
               onKeyChange(val); 
            }}
          >
            <SelectTrigger className="h-9 text-xs font-medium bg-white border-gray-200 rounded-lg hover:border-gray-300 transition-colors shadow-none focus:ring-1 focus:ring-blue-500/20">
              <SelectValue placeholder="Filter..." />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-200 shadow-lg">
              {filterDefs.map((def) => (
                <SelectItem
                  key={def.key}
                  value={def.key}
                  disabled={!availableKeys.includes(def.key) && def.key !== filter.key}
                  className="text-xs rounded-md"
                >
                   <div className="flex items-center gap-2">
                      {def.icon && <def.icon className="h-3.5 w-3.5 text-gray-400" />}
                      {def.label}
                    </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Mobile Remove Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="sm:hidden h-9 w-9 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex-shrink-0 border border-transparent hover:border-red-100"
          title="Filter entfernen"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Value Input */}
      <div className="flex-1 w-full min-w-0">
        {renderValueInput()}
      </div>

      {/* Desktop Remove Button */}
      <div className="hidden sm:block">
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
          title="Filter entfernen"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
